import React, { useContext, useMemo, useState } from "react";
import { Button, Input, Modal, Radio, Select, Upload } from "antd";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Trophy,
  Plus,
  FileText,
  Trash2,
  FileSpreadsheet,
  ExternalLink,
  Edit3,
  Pin,
  PinOff,
  Image as ImageIcon,
  Bold,
  Italic,
  List,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";
import ReportContext from "../../context/reportContext";
import ReportUploadForm from "../Reports/ReportDatabase/ReportUploadForm";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatIndianNumber = (num) => Number(num || 0).toLocaleString("en-IN");

// ─── Reusable Panel (accordion) ─────────────────────────────────────────────

export const Panel = ({ title, children, expanded, onToggle }) => (
  <div className="election-section overflow-hidden">
    <button
      type="button"
      className="election-panel-header w-full flex items-center justify-between"
      onClick={onToggle}
    >
      <span>{title}</span>
      <span
        className={`transition-transform duration-300 text-xs ${
          expanded ? "rotate-180" : ""
        }`}
      >
        ▼
      </span>
    </button>

    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        expanded ? "max-h-[1200px] opacity-100 mt-3" : "max-h-0 opacity-0"
      }`}
    >
      {children}
    </div>
  </div>
);

// ─── Election Result Panel ───────────────────────────────────────────────────

export const ElectionResultPanel = ({ party, panels, constituency, expanded, onToggle }) => (
  <Panel
    title={`Election Result - ${party}`}
    expanded={expanded}
    onToggle={onToggle}
  >
    <div className="card-wrapper !rounded-xl p-3 mb-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <Trophy size={14} />
            Rank
          </div>
          <div className="text-3xl font-bold text-[#0b65c2]">
            #{panels.electionResult?.rank || "-"}
          </div>
        </div>
        {panels.electionResult?.category && (
          <div className="text-right">
            <div className="text-xs text-gray-600">Category</div>
            <div className="text-sm font-bold text-[#0b65c2]">
              {panels.electionResult.category}
            </div>
          </div>
        )}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="card-wrapper !rounded-xl px-3 py-2 shadow-sm">
        <div className="text-sm text-gray-600">Candidate</div>
        <div className="text-sm font-semibold text-[#0b65c2]">
          {panels.electionResult?.candidateName || "-"}
        </div>
      </div>
      <div className="card-wrapper !rounded-xl px-3 py-2 shadow-sm">
        <div className="text-sm text-gray-600">Vote Share</div>
        <div className="text-lg font-semibold text-[#0b65c2]">
          {formatIndianNumber(panels.electionResult?.voteShare)}
        </div>
        <div className="text-xs text-black">
          {panels.electionResult?.voteSharePercent}%
        </div>
      </div>
    </div>

    {constituency && (
      <>
        <div
          className="card-wrapper !rounded-xl px-3 py-2 mt-2 shadow-sm"
          style={{
            border: `1px solid ${
              Number(panels.electionResult?.marginPercent) >= 0 ? "#16a34a" : "#dc2626"
            }55`,
          }}
        >
          <div className="text-sm text-gray-600">Margin</div>
          <div
            className="text-lg font-bold"
            style={{
              color:
                Number(panels.electionResult?.marginPercent) >= 0
                  ? "#16a34a"
                  : "#dc2626",
            }}
          >
            {Number(panels.electionResult?.marginPercent) >= 0 ? "Won by" : "Lost by"}{" "}
            {Math.abs(Number(panels.electionResult?.marginPercent) || 0)}%
          </div>
        </div>
        <LocationsPanel
          panels={panels}
          constituency={constituency}
          expanded
          onToggle={() => {}}
        />
      </>
    )}
  </Panel>
);

// ─── Seat Status Panel ───────────────────────────────────────────────────────

const SEAT_STYLES = {
  safe: {
    bg: "rgba(0, 80, 53, 0.2)",
    border: "1px solid rgba(0, 101, 67, 0.35)",
    text: "#005636ff",
  },
  battleground: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "1px solid rgba(245, 158, 11, 0.35)",
    text: "#f9b70fff",
  },
  favourable: {
    bg: "rgba(16, 185, 129, 0.12)",
    border: "1px solid rgba(16, 185, 129, 0.35)",
    text: "#22835fff",
  },
  difficult: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "1px solid rgba(239, 68, 68, 0.35)",
    text: "#f87171",
  },
  "very difficult": {
    bg: "rgba(237, 37, 37, 0.12)",
    border: "1px solid rgba(238, 32, 32, 0.35)",
    text: "#f92828ff",
  },
};

export const SeatStatusPanel = ({ panels, expanded, onToggle }) => (
  <Panel title="Seat Status" expanded={expanded} onToggle={onToggle}>
    <div className="grid grid-cols-2 gap-3 mt-2 card-wrapper p-2 rounded-xl">
      {(panels.favourability || []).map((item) => {
        const style =
          SEAT_STYLES[item.category?.toLowerCase()] || SEAT_STYLES.difficult;
        return (
          <div
            key={item.category}
            className="rounded-xl px-2 py-3 transition-all duration-200"
            style={{
              background: style.bg,
              border: style.border,
              boxShadow: "0 0 12px rgba(0,0,0,0.15)",
            }}
          >
            <div
              className="text-xl font-bold leading-none"
              style={{ color: style.text }}
            >
              {item.count}
            </div>
            <div
              className="uppercase tracking-widest text-sm font-semibold mt-2"
              style={{ color: style.text }}
            >
              {item.category}
            </div>
          </div>
        );
      })}
    </div>
  </Panel>
);

// ─── Community Distribution Panel ───────────────────────────────────────────

export const CommunityPanel = ({ communityChart, expanded, onToggle }) => (
  <Panel
    title="Community Distribution"
    expanded={expanded}
    onToggle={onToggle}
  >
    <ResponsiveContainer width="100%" height={190}>
      <BarChart data={communityChart}>
        <XAxis dataKey="name" tick={{ fill: "#9aa7c7", fontSize: 10 }} />
        <YAxis tick={{ fill: "#9aa7c7", fontSize: 10 }} />
        <Tooltip />
        <Bar dataKey="value" fill="#4f8cff" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Panel>
);

// ─── Religion Distribution Panel ────────────────────────────────────────────

const PIE_COLORS = ["#4f8cff", "#ff6b1a", "#37d67a"];

export const ReligionPanel = ({ religionChart, expanded, onToggle }) => (
  <Panel
    title="Religion Distribution"
    expanded={expanded}
    onToggle={onToggle}
  >
    <ResponsiveContainer width="100%" height={190}>
      <PieChart>
        <Pie
          data={religionChart}
          dataKey="value"
          nameKey="name"
          innerRadius={44}
          outerRadius={78}
        >
          {religionChart.map((entry, index) => (
            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </Panel>
);

// ─── Top Locations Panel ─────────────────────────────────────────────────────

export const LocationsPanel = ({
  panels,
  constituency,
  expanded,
  onToggle,
}) => {
  const maxLocation = Math.max(
    ...(panels.topLocations || []).map((item) => item.count),
    1
  );

  return (
    <Panel
      title="Top 5 Most Visited Location"
      expanded={expanded}
      onToggle={onToggle}
    >
      {constituency ? (
        <div className="election-chip-row">
          {(panels.topLocations || []).map((item) => (
            <span className="election-chip active" key={item.name}>
              {item.name}
            </span>
          ))}
        </div>
      ) : (
        (panels.topLocations || []).map((item) => (
          <div key={item.name} className="mb-3">
            <div className="election-row !border-0 !py-1">
              <span>{item.name}</span>
              <strong>{item.count}</strong>
            </div>
            <div className="election-progress">
              <div
                className="election-progress-fill"
                style={{ width: `${(item.count / maxLocation) * 100}%` }}
              />
            </div>
          </div>
        ))
      )}

      {!panels.topLocations?.length && (
        <div className="election-empty">No locations available</div>
      )}
    </Panel>
  );
};

// ─── Reports Tab ─────────────────────────────────────────────────────────────

const REPORT_STYLE = {
  bg: "rgba(79,140,255,0.12)",
  border: "rgba(79,140,255,0.35)",
  text: "#0b65c2",
};

const getReportFile = (report) => report.resourceLocation?.[0] || {};

const getFileKind = (fileName = "") => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".xls") || lower.endsWith(".xlsx") || lower.endsWith(".csv")) return "excel";
  if (lower.match(/\.(jpg|jpeg|png)$/)) return "image";
  return "document";
};

const ReportFileIcon = ({ kind }) => {
  if (kind === "excel") return <FileSpreadsheet size={14} style={{ color: "#15803d" }} />;
  return <FileText size={14} style={{ color: REPORT_STYLE.text }} />;
};

const ReportCard = ({ report, selected, onOpen, onDelete }) => {
  const file = getReportFile(report);
  const kind = getFileKind(file.name);

  return (
    <button
      type="button"
      className="card-wrapper !rounded-xl px-3 py-3 mb-2 flex items-start gap-3 w-full text-left hover:shadow-md transition-all"
      style={{ border: `1px solid ${selected ? REPORT_STYLE.text : REPORT_STYLE.border}` }}
      onClick={() => onOpen(report)}
    >
      <div
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ background: REPORT_STYLE.bg }}
      >
        <ReportFileIcon kind={kind} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate text-black">{report.reportName}</div>
        <div className="text-xs text-gray-800 mt-0.5">
          {report.constituency || "All constituencies"} · {new Date(report.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>
        {report.reportDescription && (
          <div className="text-xs text-gray-600 mt-1 line-clamp-2">
            {report.reportDescription}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: REPORT_STYLE.bg, color: REPORT_STYLE.text, border: `1px solid ${REPORT_STYLE.border}` }}
        >
          {kind}
        </span>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(report._id);
          }}
          className="text-gray-600 hover:text-red-400 transition-colors"
          title="Delete report"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </button>
  );
};

const normalizeTags = (report) => {
  const tags = Array.isArray(report.tags) ? report.tags : [];
  const flatTags = tags
    .flatMap((tag) => String(tag || "").split(","))
    .map((tag) => tag.trim())
    .filter(Boolean);
  return flatTags.length ? flatTags : ["Untagged"];
};

const ReportPreview = ({ report }) => {
  if (!report) {
    return (
      <div className="election-empty text-center py-5">
        <FileText size={24} className="mx-auto mb-2 text-gray-300" />
        <div className="text-gray-400 text-sm">Select a report to preview</div>
      </div>
    );
  }

  const file = getReportFile(report);
  const kind = getFileKind(file.name);

  return (
    <div className="card-wrapper !rounded-xl p-3 mb-3">
      <div className="flex justify-between items-start gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{report.reportName}</div>
          <div className="text-xs text-gray-500">{file.name}</div>
        </div>
        {file.publicUrl && (
          <button
            type="button"
            className="text-[#0b65c2] hover:text-[#0952a8]"
            onClick={() => window.open(file.publicUrl, "_blank", "noopener,noreferrer")}
            title="Open report"
          >
            <ExternalLink size={15} />
          </button>
        )}
      </div>

      {kind === "pdf" && file.publicUrl ? (
        <iframe title={report.reportName} src={file.publicUrl} className="w-full h-48 rounded-lg border border-gray-200" />
      ) : kind === "image" && file.publicUrl ? (
        <img src={file.publicUrl} alt={report.reportName} className="w-full max-h-48 object-contain rounded-lg border border-gray-200" />
      ) : (
        <div className="rounded-lg border border-gray-200 p-3 text-xs text-gray-500">
          {kind === "excel" ? "Excel preview is not available here. Open the report to view or download it." : "Preview is not available for this file type."}
        </div>
      )}

      {report.reportDescription && (
        <div className="text-xs text-gray-600 mt-2">{report.reportDescription}</div>
      )}
    </div>
  );
};

export const ReportsTab = ({
  electionModeId,
  filters,
  constituencyOptions,
  districts,
  electionModeType,
  urbanRural,
  quickReportRequest,
}) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const reportContext = useContext(ReportContext);
  const [selectedReport, setSelectedReport] = useState(null);
  const [expandedTags, setExpandedTags] = useState({});

  const reportsQuery = useQuery(
    ["election-mode-reports", electionModeId, filters.district, filters.constituency],
    async () => {
      const response = await axiosInstance.get("api/reports/election-mode/list", {
        params: {
          electionModeId,
          district: filters.district || undefined,
          constituency: filters.constituency || undefined,
        },
      });
      return response.data?.data || [];
    },
    {
      enabled: Boolean(electionModeId),
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const deleteReportMutation = useMutation(
    (reportId) => axiosInstance.delete(`api/reports/${reportId}`),
    {
      onSuccess: () => {
        toast.success("Report deleted successfully");
        setSelectedReport(null);
        queryClient.invalidateQueries("election-mode-reports");
      },
      onError: () => toast.error("Unable to delete report"),
    }
  );

  const reports = useMemo(() => reportsQuery.data || [], [reportsQuery.data]);
  const groupedReports = useMemo(() => {
    return reports.reduce((acc, report) => {
      normalizeTags(report).forEach((tag) => {
        acc[tag] = acc[tag] || [];
        acc[tag].push(report);
      });
      return acc;
    }, {});
  }, [reports]);

  const handleOpenReport = (report) => {
    setSelectedReport(report);
    // if (file.publicUrl) {
    //   window.open(file.publicUrl, "_blank", "noopener,noreferrer");
    // }
  };

  const handleOpenModal = () => {
    reportContext.updateAddMode(true);
    reportContext.updateOpenModal(true);
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className="w-full flex items-center justify-center gap-2 bg-[#0b65c2] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#0952a8] transition-colors shadow-sm"
        onClick={handleOpenModal}
      >
        <Plus size={15} />
        Add Report
      </button>

      <ReportPreview report={selectedReport} />

      <div className="mt-1">
        {reportsQuery.isLoading ? (
          <div className="election-empty text-center py-8">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="election-empty text-center py-8">
            <FileText size={28} className="mx-auto mb-2 text-gray-300" />
            <div className="text-gray-400 text-sm">No reports yet</div>
          </div>
        ) : (
          Object.entries(groupedReports).map(([tag, tagReports]) => {
            const expanded = expandedTags[tag] !== false;
            return (
              <Panel
                key={tag}
                title={`${tag} (${tagReports.length})`}
                expanded={expanded}
                onToggle={() =>
                  setExpandedTags((prev) => ({ ...prev, [tag]: !expanded }))
                }
              >
                {tagReports.map((report) => (
                  <ReportCard
                    key={report._id}
                    report={report}
                    selected={selectedReport?._id === report._id}
                    onOpen={handleOpenReport}
                    onDelete={(id) => deleteReportMutation.mutate(id)}
                  />
                ))}
              </Panel>
            );
          })
        )}
      </div>

      <ReportUploadForm
        isOpen={reportContext.isOpenModal}
        isAddMode
        fixedType="electionMode"
        electionModeId={electionModeId}
        constituencyOptions={constituencyOptions}
        districtOptions={districts}
        electionModeScope={electionModeType}
        electionModeUrbanRural={urbanRural}
        defaultConstituency={filters.constituency}
        hideSubscribers
        onSuccess={() => queryClient.invalidateQueries("election-mode-reports")}
      />
    </div>
  );
};

const emptyNoteForm = {
  heading: "",
  scope: "state",
  district: "",
  constituency: "",
  party: "",
  content: "",
  pinned: false,
};

const NoteEditor = ({ value, onChange }) => {
  const editorRef = React.useRef(null);

  const runCommand = (command) => {
    document.execCommand(command, false, null);
    onChange(editorRef.current?.innerHTML || "");
  };

  const insertImage = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      document.execCommand("insertImage", false, reader.result);
      onChange(editorRef.current?.innerHTML || "");
    };
    reader.readAsDataURL(file);
    return false;
  };

  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="rounded-xl border border-gray-200 bg-[#fffdf5] overflow-hidden">
      <div className="flex gap-1 border-b border-gray-200 bg-white px-2 py-2">
        <Button size="small" icon={<Bold size={14} />} onClick={() => runCommand("bold")} />
        <Button size="small" icon={<Italic size={14} />} onClick={() => runCommand("italic")} />
        <Button size="small" icon={<List size={14} />} onClick={() => runCommand("insertUnorderedList")} />
        <Upload showUploadList={false} beforeUpload={insertImage} accept="image/*">
          <Button size="small" icon={<ImageIcon size={14} />} />
        </Upload>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[220px] p-4 text-sm text-slate-800 outline-none prose max-w-none"
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        style={{ whiteSpace: "pre-wrap" }}
      />
    </div>
  );
};

const NoteModal = ({
  open,
  mode,
  form,
  setForm,
  districts,
  constituencies,
  parties,
  onCancel,
  onSave,
}) => {
  const isView = mode === "view";
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={860}
      zIndex={100000}
      getContainer={() => document.body}
      footer={
        isView
          ? null
          : [
              <Button key="cancel" onClick={onCancel}>
                Cancel
              </Button>,
              <Button key="save" type="primary" className="bg-[#0b65c2]" onClick={onSave}>
                Save Note
              </Button>,
            ]
      }
      title={isView ? form.heading : mode === "edit" ? "Edit Note" : "Add Note"}
    >
      {isView ? (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="election-chip active">{form.scope}</span>
            {form.party && <span className="election-chip">{form.party}</span>}
            {form.district && <span className="election-chip">{form.district}</span>}
            {form.constituency && <span className="election-chip">{form.constituency}</span>}
          </div>
          <div
            className="rounded-xl border border-gray-200 bg-[#fffdf5] p-4 min-h-[220px] text-slate-800"
            dangerouslySetInnerHTML={{ __html: form.content || "" }}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Heading</div>
            <Input
              value={form.heading}
              placeholder="Note heading"
              onChange={(event) => setForm((prev) => ({ ...prev, heading: event.target.value }))}
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Create note for</div>
            <Radio.Group
              value={form.scope}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  scope: event.target.value,
                  district: event.target.value === "district" ? prev.district : "",
                  constituency: event.target.value === "constituency" ? prev.constituency : "",
                }))
              }
            >
              <Radio.Button value="state">State</Radio.Button>
              <Radio.Button value="district">District</Radio.Button>
              <Radio.Button value="constituency">Constituency</Radio.Button>
            </Radio.Group>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Party</div>
              <Select
                className="w-full"
                placeholder="Select party"
                value={form.party || undefined}
                onChange={(value) => setForm((prev) => ({ ...prev, party: value }))}
                options={parties.map((party) => ({ value: party, label: party }))}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
            {form.scope === "district" && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">District</div>
                <Select
                  className="w-full"
                  placeholder="Select district"
                  value={form.district || undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, district: value }))}
                  options={districts.map((district) => ({ value: district, label: district }))}
                  showSearch
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
            )}
            {form.scope === "constituency" && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Constituency</div>
                <Select
                  className="w-full"
                  placeholder="Select constituency"
                  value={form.constituency || undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, constituency: value }))}
                  options={constituencies.map((constituency) => ({
                    value: constituency,
                    label: constituency,
                  }))}
                  showSearch
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
            )}
          </div>

          <NoteEditor
            value={form.content}
            onChange={(content) => setForm((prev) => ({ ...prev, content }))}
          />
        </div>
      )}
    </Modal>
  );
};

const MLA_FIELDS = [
  ["party", "Party"],
  ["constituency", "Constituency"],
  ["district", "District"],
  ["previousParty", "Previous Party"],
  ["fatherName", "Father's Name"],
  ["dateOfBirth", "Date of Birth"],
  ["placeOfBirth", "Place of Birth"],
  ["religion", "Religion"],
  ["caste", "Caste"],
  ["education", "Education"],
  ["business", "Business"],
  ["totalProperties", "Total Properties"],
  ["temporaryAddress", "Temporary Address"],
  ["mobileNumber", "Mobile Number"],
  ["email", "Email"],
  ["bjpStatus", "BJP Status"],
];

export const MlaAffidavitPanel = ({ electionModeId, filters }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const affidavitsQuery = useQuery(
    ["election-mode-mla-affidavits", electionModeId, filters.district, filters.constituency],
    async () => {
      const response = await axiosInstance.get(
        `api/electionMode/${electionModeId}/mla-affidavit`,
        {
          params: {
            district: filters.district || undefined,
            constituency: filters.constituency || undefined,
          },
        }
      );
      return response.data?.data || [];
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  const uploadMutation = useMutation(
    (file) => {
      const formData = new FormData();
      formData.append("file", file);
      return axiosInstance.put(
        `api/electionMode/${electionModeId}/mla-affidavit`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    },
    {
      onSuccess: () => {
        toast.success("MLA affidavit uploaded");
        queryClient.invalidateQueries("election-mode-mla-affidavits");
      },
      onError: () => toast.error("Unable to upload MLA affidavit"),
    }
  );

  const deleteMutation = useMutation(
    () => axiosInstance.delete(`api/electionMode/${electionModeId}/mla-affidavit`),
    {
      onSuccess: () => {
        toast.success("MLA affidavit deleted");
        queryClient.invalidateQueries("election-mode-mla-affidavits");
      },
      onError: () => toast.error("Unable to delete MLA affidavit"),
    }
  );

  const rows = affidavitsQuery.data || [];

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-semibold text-sm text-black">MLA Affidavit</div>
        {rows.length > 0 && (
          <Button
            danger
            size="small"
            loading={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate()}
          >
            Delete Sheet
          </Button>
        )}
      </div>

      <Upload
        beforeUpload={(file) => {
          uploadMutation.mutate(file);
          return false;
        }}
        maxCount={1}
        showUploadList={false}
        accept=".xlsx,.xls,.csv"
      >
        <Button
          block
          icon={<FileSpreadsheet size={15} />}
          loading={uploadMutation.isLoading}
        >
          {rows.length ? "Edit MLA Affidavit" : "Add MLA Affidavit"}
        </Button>
      </Upload>

      <div className="mt-3 flex flex-col gap-2">
        {affidavitsQuery.isLoading ? (
          <div className="election-empty text-center py-4">Loading MLA details...</div>
        ) : rows.length === 0 ? (
          <div className="election-empty text-center py-4">
            Select a district or constituency to view MLA details.
          </div>
        ) : (
          rows.map((row) => (
            <div key={row._id} className="card-wrapper !rounded-xl p-3">
              <div className="font-semibold text-sm text-black">{row.name || "-"}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-700">
                {MLA_FIELDS.map(([key, label]) => (
                  row[key] ? (
                    <div key={key} className="rounded-lg bg-white/70 p-2">
                      <div className="text-slate-500">{label}</div>
                      <strong>{row[key]}</strong>
                    </div>
                  ) : null
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const NotesPanel = ({
  electionModeId,
  filters,
  districts,
  constituencies,
  parties,
  quickNoteRequest,
}) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [form, setForm] = useState(emptyNoteForm);

  const notesQuery = useQuery(
    ["election-mode-notes", electionModeId, filters.district, filters.constituency, filters.party],
    async () => {
      const response = await axiosInstance.get("api/electionMode/notes/list", {
        params: {
          electionModeId,
          district: filters.district || undefined,
          constituency: filters.constituency || undefined,
          party: filters.party || undefined,
        },
      });
      return response.data?.data || [];
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  const notes = notesQuery.data || [];

  const saveMutation = useMutation(
    (payload) => {
      if (selectedNoteId) {
        return axiosInstance.put(`api/electionMode/notes/${selectedNoteId}`, payload);
      }
      return axiosInstance.post("api/electionMode/notes", payload);
    },
    {
      onSuccess: () => {
        toast.success(selectedNoteId ? "Note updated" : "Note created");
        setModalOpen(false);
        setSelectedNoteId(null);
        queryClient.invalidateQueries("election-mode-notes");
      },
      onError: () => toast.error("Unable to save note"),
    }
  );

  const deleteMutation = useMutation(
    (id) => axiosInstance.delete(`api/electionMode/notes/${id}`),
    {
      onSuccess: () => {
        toast.success("Note deleted");
        queryClient.invalidateQueries("election-mode-notes");
      },
      onError: () => toast.error("Unable to delete note"),
    }
  );

  const openAdd = (preset = {}) => {
    setSelectedNoteId(null);
    setModalMode("add");
    setForm({
      ...emptyNoteForm,
      party: filters.party || "",
      ...preset,
    });
    setModalOpen(true);
  };


  const openEdit = (note) => {
    setSelectedNoteId(note._id);
    setModalMode("edit");
    setForm({ ...emptyNoteForm, ...note });
    setModalOpen(true);
  };

  const openView = (note) => {
    setSelectedNoteId(note._id);
    setModalMode("view");
    setForm({ ...emptyNoteForm, ...note });
    setModalOpen(true);
  };

  const togglePin = (note) => {
    setSelectedNoteId(note._id);
    saveMutation.mutate({ ...note, pinned: !note.pinned });
  };

  const handleSave = () => {
    if (!form.heading.trim()) {
      toast.error("Heading is required");
      return;
    }
    if (form.scope === "district" && !form.district) {
      toast.error("District is required");
      return;
    }
    if (form.scope === "constituency" && !form.constituency) {
      toast.error("Constituency is required");
      return;
    }
    saveMutation.mutate({
      ...form,
      electionModeId,
    });
  };

  return (
    <div className="election-dashboard-right card-wrapper">
      <button
        type="button"
        className="w-full flex items-center justify-center gap-2 bg-[#0b65c2] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#0952a8] transition-colors shadow-sm"
        onClick={() => openAdd()}
      >
        <Plus size={15} />
        Add Note
      </button>

      <div className="mt-3 flex flex-col gap-2">
        {notesQuery.isLoading ? (
          <div className="election-empty text-center py-8">Loading notes...</div>
        ) : notes.length === 0 ? (
          <div className="election-empty text-center py-8">No notes yet</div>
        ) : (
          notes.map((note) => (
            <button
              type="button"
              key={note._id}
              onClick={() => openView(note)}
              className="card-wrapper !rounded-xl p-3 text-left border border-white/40 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-black truncate">
                    {note.pinned ? "Pinned · " : ""}
                    {note.heading}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {note.scope}
                    {note.district ? ` · ${note.district}` : ""}
                    {note.constituency ? ` · ${note.constituency}` : ""}
                    {note.party ? ` · ${note.party}` : ""}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="small"
                    icon={note.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      togglePin(note);
                    }}
                  />
                  <Button
                    size="small"
                    icon={<Edit3 size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEdit(note);
                    }}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<Trash2 size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteMutation.mutate(note._id);
                    }}
                  />
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      <NoteModal
        open={modalOpen}
        mode={modalMode}
        form={form}
        setForm={setForm}
        districts={districts}
        constituencies={constituencies}
        parties={parties}
        onCancel={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
};

// ─── Tab Bar ─────────────────────────────────────────────────────────────────

const TABS = ["Electoral", "Reports"];

export const TabBar = ({ activeTab, onTabChange }) => (
  <div className="flex gap-1 mb-3 p-1 bg-gray-100 rounded-xl">
    {TABS.map((tab) => (
      <button
        key={tab}
        type="button"
        onClick={() => onTabChange(tab)}
        className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
          activeTab === tab
            ? "bg-white text-[#0b65c2] shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {tab}
      </button>
    ))}
  </div>
);

// ─── Electoral Tab ────────────────────────────────────────────────────────────

export const ElectoralTab = ({
  filters,
  panels,
  communityChart,
  religionChart,
  expandedPanels,
  onToggle,
}) => (
  <>
    <ElectionResultPanel
      party={filters.party}
      panels={panels}
      constituency={filters.constituency}
      expanded={expandedPanels.electionResult}
      onToggle={() => onToggle("electionResult")}
    />
    {!filters.constituency && (
      <SeatStatusPanel
        panels={panels}
        expanded={expandedPanels.favourability}
        onToggle={() => onToggle("favourability")}
      />
    )}
    <CommunityPanel
      communityChart={communityChart}
      expanded={expandedPanels.community}
      onToggle={() => onToggle("community")}
    />
    {/* <ReligionPanel
      religionChart={religionChart}
      expanded={expandedPanels.religion}
      onToggle={() => onToggle("religion")}
    /> */}
    {!filters.constituency && (
      <LocationsPanel
        panels={panels}
        constituency={filters.constituency}
        expanded={expandedPanels.locations}
        onToggle={() => onToggle("locations")}
      />
    )}
  </>
);

// ─── Right Panel (main export) ────────────────────────────────────────────────

const RightPanel = ({
  electionModeId,
  filters,
  constituencyOptions,
  districts,
  electionModeType,
  urbanRural,
  panels,
  communityChart,
  religionChart,
  expandedPanels,
  onToggle,
  activeTab,
  onActiveTabChange,
  quickReportRequest,
}) => {
  return (
    <div className="election-dashboard-right card-wrapper">
      <TabBar activeTab={activeTab} onTabChange={onActiveTabChange} />

      {activeTab === "Electoral" ? (
        <ElectoralTab
          filters={filters}
          panels={panels}
          communityChart={communityChart}
          religionChart={religionChart}
          expandedPanels={expandedPanels}
          onToggle={onToggle}
        />
      ) : (
        <ReportsTab
          electionModeId={electionModeId}
          filters={filters}
          constituencyOptions={constituencyOptions}
          districts={districts}
          electionModeType={electionModeType}
          urbanRural={urbanRural}
          quickReportRequest={quickReportRequest}
        />
      )}
    </div>
  );
};

export const NewsPanelAdmin = ({ electionModeId }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const [filterCategory, setFilterCategory] = useState("");
  const [uploadingFiles, setUploadingFiles] = useState([]);

  const newsQuery = useQuery(
    ["election-mode-news", electionModeId],
    async () => {
      const res = await axiosInstance.get(`api/electionMode/${electionModeId}/news`);
      return res.data?.data || [];
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  // Upload a single file — append flag controls whether existing data is cleared first
  const uploadFile = async (file, append) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosInstance.put(
      `api/electionMode/${electionModeId}/news?append=${append}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  };

  // Handle multiple files: first file replaces, subsequent files append
  const handleUpload = async (files) => {
    setUploadingFiles(files.map((f) => f.name));
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadFile(files[i], i > 0); // append=false for first, true for rest
      }
      toast.success(`${files.length} file(s) uploaded successfully`);
      queryClient.invalidateQueries("election-mode-news");
    } catch {
      toast.error("One or more files failed to upload");
    } finally {
      setUploadingFiles([]);
    }
  };

  const deleteMutation = useMutation(
    () => axiosInstance.delete(`api/electionMode/${electionModeId}/news`),
    {
      onSuccess: () => {
        toast.success("All news deleted");
        queryClient.invalidateQueries("election-mode-news");
      },
      onError: () => toast.error("Unable to delete news"),
    }
  );

  const allRows = newsQuery.data || [];

  // Derive unique categories for filter dropdown
  const categories = useMemo(
    () => [...new Set(allRows.map((r) => r.category).filter(Boolean))].sort(),
    [allRows]
  );

  const rows = filterCategory
    ? allRows.filter((r) => r.category === filterCategory)
    : allRows;

  const isUploading = uploadingFiles.length > 0;

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-semibold text-sm text-black">
          News (Samachar)
          {allRows.length > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-400">{allRows.length} records</span>
          )}
        </div>
        {allRows.length > 0 && (
          <Button
            danger
            size="small"
            loading={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate()}
          >
            Delete All
          </Button>
        )}
      </div>

      {/* Upload — multiple files allowed */}
      <Upload
        multiple
        beforeUpload={() => false}
        accept=".xlsx,.xls,.csv"
        showUploadList={false}
        onChange={({ fileList }) => {
          if (!fileList.length) return;
          const rawFiles = fileList.map((f) => f.originFileObj).filter(Boolean);
          if (rawFiles.length) handleUpload(rawFiles);
        }}
      >
        <Button block icon={<FileSpreadsheet size={15} />} loading={isUploading}>
          {isUploading
            ? `Uploading ${uploadingFiles.length} file(s)…`
            : allRows.length
            ? "Add / Append News Sheet(s)"
            : "Add News Sheet(s)"}
        </Button>
      </Upload>

      <div className="mt-1 text-xs text-slate-400">
        Multiple files: first replaces existing data, each additional appends. Columns: District, BJP
        Kshetra, cons_name, Category, Samachar, Date, Time
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <Select
          allowClear
          placeholder="Filter by Category"
          className="w-full mt-3"
          size="small"
          value={filterCategory || undefined}
          onChange={(v) => setFilterCategory(v || "")}
          options={categories.map((c) => ({ label: c, value: c }))}
        />
      )}

      {/* News list */}
      <div className="mt-3 flex flex-col gap-2">
        {newsQuery.isLoading ? (
          <div className="election-empty text-center py-4">Loading news…</div>
        ) : rows.length === 0 ? (
          <div className="election-empty text-center py-4">
            {allRows.length ? "No news match the selected category." : "No news uploaded yet."}
          </div>
        ) : (
          rows.slice(0, 15).map((row) => (
            <div key={row._id} className="card-wrapper !rounded-xl p-3">
              <div className="text-xs text-slate-700 mb-1 leading-snug">{row.samachar || "-"}</div>
              <div className="flex flex-wrap gap-1 text-[11px] text-slate-500">
                {row.district && (
                  <span className="bg-slate-100 px-2 py-0.5 rounded">{row.district}</span>
                )}
                {row.cons_name && (
                  <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{row.cons_name}</span>
                )}
                {row.bjpKshetra && (
                  <span className="bg-orange-50 text-orange-500 px-2 py-0.5 rounded">{row.bjpKshetra}</span>
                )}
                {row.category && (
                  <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{row.category}</span>
                )}
                {row.date && (
                  <span className="ml-auto text-slate-400">
                    {row.date} {row.time || ""}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
        {rows.length > 15 && (
          <div className="text-center text-xs text-slate-400">
            +{rows.length - 15} more records
          </div>
        )}
      </div>
    </div>
  );
};

export default RightPanel;
